import React from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

const Section = ({ title, children }) => (
  <div className="mb-8">
    <h2 className="text-lg font-semibold text-gray-900 mb-3">{title}</h2>
    <div className="text-sm text-gray-700 leading-relaxed space-y-3">{children}</div>
  </div>
);

const PrivacyPage = () => (
  <>
    <Helmet><title>Privacy Policy - MME</title></Helmet>

    <div className="min-h-screen bg-[#F9F7F4] py-16 px-4">
      <div className="max-w-3xl mx-auto">
        <Link to="/" className="mme-link text-sm hover:underline mb-8 inline-block">&larr; Back to home</Link>

        <h1 className="text-3xl font-bold text-gray-900 mb-1">Privacy Policy</h1>
        <p className="text-sm text-gray-500 mb-10">Effective: April 10, 2026 &middot; Last updated: April 11, 2026</p>

        <Section title="1. Who We Are">
          <p>
            Railtech Inc. (&quot;Railtech,&quot; &quot;we,&quot; &quot;us,&quot; or &quot;our&quot;) is a company
            incorporated in Ontario, Canada. We operate the MME (Memory Management Engine) service, a
            persistent memory layer for AI applications. This Privacy Policy explains how we collect, use,
            store, and protect your personal information when you use MME.
          </p>
        </Section>

        <Section title="2. Information We Collect">
          <p><strong>Account information:</strong> When you sign in with Google, we receive your name, email
          address, and profile picture from Google. We do not access your Gmail, Google Drive, contacts, or
          any other Google services.</p>

          <p><strong>Memory content:</strong> Any text content you save through the MME API, Claude Desktop
          integration, or other clients. This is the core data you choose to store in MME.</p>

          <p><strong>API usage metadata:</strong> Timestamps, request counts, endpoint paths, response status
          codes, and IP addresses. Used for rate limiting, abuse prevention, and service monitoring.</p>

          <p><strong>API keys:</strong> When you create an API key, it is stored in our database and associated
          with your account. API keys are displayed to you once in the dashboard and are stored in plaintext
          (not hashed) so you can view them later.</p>

          <p><strong>Session data:</strong> We use browser sessionStorage (not cookies) to store your
          authentication token during a browser session. This data is automatically cleared when you close the
          tab. We do not use tracking cookies, advertising cookies, or persistent browser storage.</p>
        </Section>

        <Section title="3. How We Use Your Information">
          <p>We use your information solely to provide and improve the MME service:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>Authenticate your identity and manage your account</li>
            <li>Store, tag, and retrieve your memory content</li>
            <li>Process memory content through AI for automatic tag extraction</li>
            <li>Enforce rate limits and prevent abuse</li>
            <li>Monitor service health and diagnose issues</li>
            <li>Notify you of important service changes</li>
          </ul>
          <p>We do not use your data for advertising, profiling, or any purpose unrelated to delivering the
          MME service.</p>
        </Section>

        <Section title="4. AI Processing and Third-Party Data Sharing">
          <p>When you save a memory, its content is sent to the <strong>Anthropic Claude API</strong> for
          automatic tag extraction. This means Anthropic processes your memory content to generate semantic
          tags. Under Anthropic&apos;s commercial API terms, data sent through their API is <strong>not used
          to train their models</strong>. For details, see{' '}
          <a href="https://www.anthropic.com/privacy" target="_blank" rel="noopener noreferrer" className="mme-link hover:underline">
            Anthropic&apos;s Privacy Policy
          </a>.</p>
          <p>We do not sell, rent, or share your personal information with any other third parties for their
          own purposes.</p>
        </Section>

        <Section title="5. Service Providers (Sub-processors)">
          <p>We use the following service providers to operate MME:</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm border border-gray-200 rounded-lg">
              <thead>
                <tr className="bg-gray-50 text-left">
                  <th className="px-4 py-2 font-medium text-gray-700 border-b">Provider</th>
                  <th className="px-4 py-2 font-medium text-gray-700 border-b">Purpose</th>
                  <th className="px-4 py-2 font-medium text-gray-700 border-b">Location</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b"><td className="px-4 py-2">Google Cloud Platform</td><td className="px-4 py-2">Backend hosting, database, compute</td><td className="px-4 py-2">us-central1 (Iowa, US)</td></tr>
                <tr className="border-b"><td className="px-4 py-2">Anthropic</td><td className="px-4 py-2">AI tag extraction (Claude API)</td><td className="px-4 py-2">United States</td></tr>
                <tr className="border-b"><td className="px-4 py-2">Vercel</td><td className="px-4 py-2">Frontend hosting and CDN</td><td className="px-4 py-2">Global edge network</td></tr>
                <tr><td className="px-4 py-2">UptimeRobot</td><td className="px-4 py-2">Uptime monitoring (pings health endpoint only)</td><td className="px-4 py-2">United States</td></tr>
              </tbody>
            </table>
          </div>
        </Section>

        <Section title="6. Data Storage and International Transfers">
          <p>Your data is stored on Google Cloud Platform in the <strong>us-central1</strong> region (Iowa,
          United States). If you are located outside the United States, your data will be transferred to and
          processed in the US. By using MME, you consent to this transfer. We rely on Google Cloud&apos;s
          compliance certifications (SOC 2, ISO 27001) and contractual protections to safeguard your data.</p>
        </Section>

        <Section title="7. Data Retention">
          <p>We retain your data for as long as your account is active:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Memory content and tags:</strong> Retained until you delete individual memories or request full account deletion.</li>
            <li><strong>Account information:</strong> Retained until you request account deletion.</li>
            <li><strong>Server logs (IP addresses, request metadata):</strong> Retained for 30 days, then automatically purged.</li>
            <li><strong>Database backups:</strong> Retained for 7 days on a rolling basis.</li>
          </ul>
        </Section>

        <Section title="8. Your Rights">
          <p>You have the following rights regarding your personal information:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li><strong>Access:</strong> Request a copy of all data we hold about you.</li>
            <li><strong>Deletion:</strong> Request deletion of your account and all associated data. We will complete deletion within 30 days and confirm via email.</li>
            <li><strong>Correction:</strong> Request correction of inaccurate personal information.</li>
            <li><strong>Data portability:</strong> Request an export of your memories in a machine-readable format (JSON).</li>
            <li><strong>Withdraw consent:</strong> You may stop using the service at any time. Deleting your account removes your data.</li>
          </ul>
          <p>To exercise any of these rights, email{' '}
            <a href="mailto:info@railtech.io" className="mme-link hover:underline">info@railtech.io</a>{' '}
            with the subject line &quot;Privacy Request.&quot; We will respond within 30 days.
          </p>
        </Section>

        <Section title="9. Data Security">
          <p>We implement reasonable security measures to protect your data:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>All data in transit is encrypted via TLS (HTTPS)</li>
            <li>Database access is restricted to authenticated services only</li>
            <li>MongoDB and Redis ports are firewalled from public internet access</li>
            <li>API keys are rate-limited to prevent brute-force attacks</li>
            <li>Authentication tokens expire after 7 days</li>
            <li>Cross-tenant data isolation is enforced at the database query level</li>
          </ul>
          <p>No system is 100% secure. If we become aware of a data breach that affects your personal
          information, we will notify you via email within 72 hours, as required by PIPEDA.</p>
        </Section>

        <Section title="10. Children's Privacy">
          <p>MME is not intended for use by anyone under the age of 16. We do not knowingly collect personal
          information from children. If you believe a child has provided us with personal information, please
          contact us and we will promptly delete it.</p>
        </Section>

        <Section title="11. PIPEDA Compliance">
          <p>As a Canadian company, we comply with Canada&apos;s Personal Information Protection and Electronic
          Documents Act (PIPEDA). This means:</p>
          <ul className="list-disc list-inside space-y-1 ml-2">
            <li>We collect personal information only for identified purposes</li>
            <li>We obtain meaningful consent for collection, use, and disclosure</li>
            <li>We limit collection to what is necessary for the identified purposes</li>
            <li>We protect personal information with appropriate safeguards</li>
            <li>We are transparent about our policies and practices</li>
            <li>We provide individuals with access to their personal information upon request</li>
          </ul>
          <p>You may file a complaint with the Office of the Privacy Commissioner of Canada at{' '}
            <a href="https://www.priv.gc.ca" target="_blank" rel="noopener noreferrer" className="mme-link hover:underline">priv.gc.ca</a>{' '}
            if you believe your privacy rights have been violated.
          </p>
        </Section>

        <Section title="12. Changes to This Policy">
          <p>We may update this Privacy Policy from time to time. We will notify you of material changes by
          posting the updated policy on this page with a new &quot;Last updated&quot; date. For significant
          changes, we will notify you via the email address associated with your account. Continued use of MME
          after changes are posted constitutes acceptance of the revised policy.</p>
        </Section>

        <Section title="13. Contact Us">
          <p>If you have questions about this Privacy Policy, want to exercise your rights, or have a privacy
          concern, please contact us:</p>
          <div className="bg-white border border-gray-200 rounded-lg p-4 mt-2">
            <p className="font-medium text-gray-900">Railtech Inc.</p>
            <p>Email:{' '}<a href="mailto:info@railtech.io" className="mme-link hover:underline">info@railtech.io</a></p>
            <p>Ontario, Canada</p>
          </div>
        </Section>
      </div>
    </div>
  </>
);

export default PrivacyPage;
